import { useEffect, useRef, type RefObject } from "react";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  isDataRoomDragData,
  type DataRoomDropTargetData,
} from "./types";

export const DROP_OVER_ATTR = "data-drop-over";

type UseFolderDropTargetArgs = {
  folderId: string;
  folderName: string;
  enabled: boolean;
  rowRef: RefObject<HTMLElement | null>;
  selectedFolders: string[];
  onDrop: (args: {
    targetFolderId: string;
    targetFolderName: string;
    fileIds: string[];
    folderIds: string[];
  }) => void;
};

function setDropOver(element: HTMLElement, isOver: boolean) {
  if (isOver) {
    element.setAttribute(DROP_OVER_ATTR, "true");
  } else {
    element.removeAttribute(DROP_OVER_ATTR);
  }
}

export function clearAllFolderDropHighlights(root?: ParentNode | null) {
  const scope = root ?? document;
  scope.querySelectorAll(`[${DROP_OVER_ATTR}="true"]`).forEach((el) => {
    el.removeAttribute(DROP_OVER_ATTR);
  });
}

export function useFolderDropTarget(args: UseFolderDropTargetArgs) {
  const { folderId, folderName, enabled, rowRef, selectedFolders, onDrop } =
    args;

  const selectedFoldersRef = useRef(selectedFolders);
  selectedFoldersRef.current = selectedFolders;

  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  useEffect(() => {
    const element = rowRef.current;
    if (!element || !enabled) {
      return;
    }

    return dropTargetForElements({
      element,
      getData: (): DataRoomDropTargetData => ({
        type: "folder",
        folderId,
        folderName,
      }),
      canDrop: ({ source }) => {
        if (!isDataRoomDragData(source.data)) {
          return false;
        }

        if (source.data.folderIds.includes(folderId)) {
          return false;
        }

        if (selectedFoldersRef.current.includes(folderId)) {
          return false;
        }

        return true;
      },
      getDropEffect: () => "move",
      onDragEnter: () => {
        setDropOver(element, true);
      },
      onDragLeave: () => {
        setDropOver(element, false);
      },
      onDrop: ({ source }) => {
        setDropOver(element, false);

        if (!isDataRoomDragData(source.data)) {
          return;
        }

        onDropRef.current({
          targetFolderId: folderId,
          targetFolderName: folderName,
          fileIds: source.data.fileIds,
          folderIds: source.data.folderIds,
        });
      },
    });
  }, [folderId, folderName, enabled, rowRef]);
}
